// Section navigation for Settings: a horizontal tab bar across the top of the
// page that scrolls sideways when it doesn't fit. Plus the heading each section opens with.
import { useEffect, useRef } from 'react'
import { indicatorStyle, useIndicator, useReducedMotion } from './settingsMotion'
import { DOMAIN_STATUS } from './useCustomDomain'

const STATUS_TEXT = {
  amber: 'text-amber-600 dark:text-amber-400',
  blue: 'text-blue-600 dark:text-blue-400',
  violet: 'text-violet-600 dark:text-violet-400',
  emerald: 'text-emerald-600 dark:text-emerald-400',
  red: 'text-red-600 dark:text-red-400',
}

// What sits at the end of a tab: an unsaved dot, or the domain's status.
function useTrailing(dirty, domainStatus) {
  const status = domainStatus ? DOMAIN_STATUS[domainStatus] : null
  return id => {
    if (dirty.has(id)) return { kind: 'dirty', title: 'Unsaved changes' }
    if (id === 'domain' && status) return { kind: 'status', tone: status.tone, text: status.short, title: status.short }
    return null
  }
}

export function SettingsNav({ sections, active, onChange, dirty, domainStatus }) {
  const barRef = useRef(null)
  const reduced = useReducedMotion()
  const box = useIndicator(barRef, active)
  const trailing = useTrailing(dirty, domainStatus)

  // Keep the active tab in view when the bar scrolls.
  useEffect(() => {
    const row = barRef.current
    const tab = row?.querySelector(`[data-key="${active}"]`)
    if (!row || !tab || row.scrollWidth <= row.clientWidth) return
    row.scrollTo({ left: tab.offsetLeft - (row.clientWidth - tab.offsetWidth) / 2, behavior: reduced ? 'auto' : 'smooth' })
  }, [active, reduced])

  return (
    <nav aria-label="Settings sections" className="mb-6">
      <div
        ref={barRef}
        className="no-scrollbar relative flex gap-1 overflow-x-auto rounded-2xl border border-gray-200 bg-white p-1.5
                   shadow-[0_1px_2px_rgba(16,24,40,0.04)] dark:border-gray-800 dark:bg-gray-900"
      >
        <span
          aria-hidden="true"
          className="absolute bottom-1.5 left-0 top-1.5 rounded-xl bg-navy-50 ring-1 ring-inset ring-navy-100
                     dark:bg-navy-900/40 dark:ring-navy-800/60"
          style={indicatorStyle(box, 'x', reduced)}
        />
        {sections.map(({ id, label, blurb, icon: Icon }) => {
          const on = id === active
          const end = trailing(id)
          return (
            <button
              key={id}
              data-key={id}
              type="button"
              onClick={() => onChange(id)}
              aria-current={on ? 'page' : undefined}
              aria-label={end ? `${label} (${end.title})` : undefined}
              title={blurb}
              className={`group relative z-[1] flex min-w-fit shrink-0 items-center gap-3 whitespace-nowrap rounded-xl px-3.5 py-2 text-left
                          transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-navy-500
                          lg:flex-1 lg:justify-center
                          ${on ? '' : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'}`}
            >
              <span className={`grid h-8 w-8 shrink-0 place-items-center rounded-lg transition-colors duration-300
                                ${on
                                  ? 'bg-white text-navy-700 shadow-sm dark:bg-navy-800 dark:text-navy-100'
                                  : 'bg-gray-100 text-gray-500 group-hover:text-gray-700 dark:bg-gray-800 dark:text-gray-400 dark:group-hover:text-gray-200'}`}>
                <Icon className="h-4 w-4" />
              </span>
              <span className="min-w-0">
                <span className="flex items-center gap-2">
                  <span className={`text-[13.5px] font-semibold transition-colors
                                    ${on ? 'text-navy-900 dark:text-navy-50' : 'text-gray-700 dark:text-gray-300'}`}>
                    {label}
                  </span>
                  {end?.kind === 'dirty' && <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-amber-500 animate-panel-in" title={end.title} />}
                  {end?.kind === 'status' && <span className={`shrink-0 text-[11px] font-medium ${STATUS_TEXT[end.tone]}`}>{end.text}</span>}
                </span>
                <span className="hidden text-xs text-gray-400 dark:text-gray-500 xl:block">{blurb}</span>
              </span>
            </button>
          )
        })}
      </div>
    </nav>
  )
}

export function SectionHeader({ section, dirty }) {
  const Icon = section.icon
  return (
    <div className="mb-4 flex items-start gap-3">
      <span className="grid h-9 w-9 shrink-0 place-items-center rounded-lg bg-navy-50 text-navy-700 ring-1 ring-inset ring-navy-100
                       dark:bg-navy-900/40 dark:text-navy-200 dark:ring-navy-800/60">
        <Icon className="h-[18px] w-[18px]" />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">{section.label}</h2>
          {dirty && (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700 ring-1 ring-inset ring-amber-200
                             animate-panel-in dark:bg-amber-900/20 dark:text-amber-300 dark:ring-amber-800/60">
              <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unsaved changes
            </span>
          )}
        </div>
        <p className="mt-0.5 text-[13px] text-gray-500 dark:text-gray-400">{section.description}</p>
      </div>
    </div>
  )
}
